import { Component } from '@theme/component';
import { debounce, requestIdleCallback } from '@theme/utilities';

/**
 * A custom element that manages the popover + popover trigger relationship for anchoring.
 * Calculates the trigger position and inlines custom properties on the popover element
 * that can be consumed by CSS for positioning.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} popover – The popover element.
 * @property {HTMLElement} trigger – The popover trigger element.
 *
 * @extends Component<Refs>
 *
 * @example
 * ```html
 * <anchored-popover-component data-close-on-resize>
 *   <button data-ref="trigger" popovertarget="menu">Open Menu</button>
 *   <div data-ref="popover" id="menu" popover>Menu content</div>
 * </anchored-popover-component>
 * ```
 *
 * @property {string[]} requiredRefs - Required refs: 'popover' and 'trigger'
 * @property {number} [interaction_delay] - The delay in milliseconds for the hover interaction
 * @property {string} [data-close-on-resize] - When present, closes popover on window resize
 * @property {string} [data-hover-triggered] - When present, makes the popover function via pointerenter/leave
 * @property {number | null} [popoverTrigger] - The timeout for the popover trigger
 */
export class AnchoredPopoverComponent extends Component {
  requiredRefs = ['popover', 'trigger'];
  interaction_delay = 200;
  #popoverTrigger = /** @type {number | null} */ (null);

  #onTriggerEnter = () => {
    const { trigger, popover } = this.refs;
    trigger.dataset.hoverActive = 'true';
    if (!popover.matches(':popover-open')) {
      this.#popoverTrigger = setTimeout(() => {
        if (trigger.matches('[data-hover-active]')) popover.showPopover();
      }, this.interaction_delay);
    }
  };

  #onTriggerLeave = () => {
    const { trigger, popover } = this.refs;
    delete trigger.dataset.hoverActive;
    if (this.#popoverTrigger) clearTimeout(this.#popoverTrigger);
    if (popover.matches(':popover-open')) {
      this.#popoverTrigger = setTimeout(() => {
        popover.hidePopover();
      }, this.interaction_delay);
    }
  };

  #onPopoverEnter = () => {
    if (this.#popoverTrigger) clearTimeout(this.#popoverTrigger);
  };

  #onPopoverLeave = () => {
    const { popover } = this.refs;
    this.#popoverTrigger = setTimeout(() => {
      popover.hidePopover();
    }, this.interaction_delay);
  };

  /**
   * Updates the popover position by calculating trigger element bounds
   * and setting CSS custom properties on the popover element.
   */
  #updatePosition = async () => {
    const { popover, trigger } = this.refs;
    if (!popover || !trigger) return;
    const positions = trigger.getBoundingClientRect();
    popover.style.setProperty('--anchor-top', `${positions.top}`);
    popover.style.setProperty('--anchor-right', `${window.innerWidth - positions.right}`);
    popover.style.setProperty('--anchor-bottom', `${window.innerHeight - positions.bottom}`);
    popover.style.setProperty('--anchor-left', `${positions.left}`);
    popover.style.setProperty('--anchor-height', `${positions.height}`);
    popover.style.setProperty('--anchor-width', `${positions.width}`);
  };

  #openWidth = 0;

  /**
   * Debounced resize handler that optionally closes the popover
   * when the window is resized, based on the data-close-on-resize attribute.
   */
  #resizeListener = debounce(() => {
    // Mobile browsers fire resize when the toolbar hides on scroll, which changes only the height.
    if (window.innerWidth === this.#openWidth) return;
    const popover = /** @type {HTMLElement} */ (this.refs.popover);
    if (popover && popover.matches(':popover-open')) {
      popover.hidePopover();
    }
  }, 100);

  /**
   * Keeps the trigger's aria-expanded in sync with the popover.
   * @param {Event} event
   */
  #onToggle = (event) => {
    if (event.target !== this.refs.popover) return;
    const evt = /** @type {ToggleEvent} */ (event);
    this.refs.trigger?.setAttribute('aria-expanded', String(evt.newState === 'open'));
  };

  /**
   * Adds the resize and scroll listeners while the popover is open.
   * @param {Event} event
   */
  #onBeforeToggle = (event) => {
    if (event.target !== this.refs.popover) return;
    const opening = /** @type {ToggleEvent} */ (event).newState === 'open';
    const method = opening ? 'addEventListener' : 'removeEventListener';
    // On the document, because Safari leaves focus on the body when the trigger is clicked.
    document[method]('keydown', this.#onKeyDown, { capture: true });
    if (this.dataset.closeOnResize) {
      this.#openWidth = window.innerWidth;
      window[method]('resize', this.#resizeListener);
    }
    if (!CSS.supports('position-anchor: --trigger')) {
      if (opening) this.#updatePosition();
      window[method]('scroll', this.#updatePosition, { capture: true, passive: true });
    }
  };

  /**
   * Closes only this popover on Escape.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    const { popover } = this.refs;
    if (event.key !== 'Escape' || !popover?.matches(':popover-open')) return;
    // An enclosing dialog, such as quick add, would otherwise close on the same keypress.
    event.preventDefault();
    event.stopImmediatePropagation();
    popover.hidePopover();
  };

  /**
   * Closes the popover when focus moves out of the component, based on the data-close-on-focusout attribute.
   * @param {FocusEvent} event
   */
  #onFocusOut = (event) => {
    const { popover } = this.refs;
    const next = event.relatedTarget;
    if (next instanceof Node && !this.contains(next) && popover?.matches(':popover-open')) {
      popover.hidePopover();
    }
  };

  /**
   * Component initialization - sets up event listeners for resize and popover toggle events.
   */
  connectedCallback() {
    super.connectedCallback();
    const { popover, trigger } = this.refs;
    // Safari doesn't expose the expanded state implied by popovertarget.
    trigger.setAttribute('aria-expanded', 'false');
    // Listen on the host: a re-render can replace the popover element but keeps this one.
    this.addEventListener('toggle', this.#onToggle, { capture: true });
    this.addEventListener('beforetoggle', this.#onBeforeToggle, { capture: true });
    if (this.dataset.closeOnFocusout) {
      this.addEventListener('focusout', this.#onFocusOut);
    }
    if (this.dataset.hoverTriggered) {
      trigger.addEventListener('pointerenter', this.#onTriggerEnter);
      trigger.addEventListener('pointerleave', this.#onTriggerLeave);
      popover.addEventListener('pointerenter', this.#onPopoverEnter);
      popover.addEventListener('pointerleave', this.#onPopoverLeave);
    }
    if (!CSS.supports('position-anchor: --trigger')) {
      requestIdleCallback(() => {
        this.#updatePosition();
      });
    }
  }

  /**
   * Component cleanup - removes the window listeners.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    window.removeEventListener('scroll', this.#updatePosition, { capture: true });
    document.removeEventListener('keydown', this.#onKeyDown, { capture: true });
  }
}

if (!customElements.get('anchored-popover-component')) {
  customElements.define('anchored-popover-component', AnchoredPopoverComponent);
}
